/**
 * The single HTTP entry point of the app.
 *
 * Anything talking to the server goes through `createApiClient(...)`. This
 * is *the* place where transport details live: base URL, auth header
 * injection, CSRF, cookies, single-flight refresh, error normalisation,
 * response schema validation.
 *
 * The client itself is dependency-free regarding *where* tokens come from —
 * stores are injected via the dependency object. This keeps the client
 * testable without Pinia and lets the auth feature plug in once it's
 * written (Phase 2).
 */
import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios'
import type { ZodTypeAny, z } from 'zod'

import {
  ApiError,
  isNonJsonResponseMarker,
  networkError,
  nonJsonResponseError,
  parseError,
  parseJsonResponse,
  timeoutError,
  toApiError,
} from './errors'
import { authPaths, isAuthOpenPath, requiresCookies } from './paths'
import {
  createRefreshCoordinator,
  type RefreshCoordinator,
  type RefreshResult,
} from './refresh-coordinator'

/** Methods we issue. */
type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'

/** Resources the client needs to read at request time. */
export interface ApiClientDeps {
  /** Returns the current access token or `null` if unauthenticated. */
  getAccessToken: () => string | null
  /** Returns the current CSRF token (web only). */
  getCsrfToken: () => string | null
  /**
   * Trigger a refresh round-trip. Must update tokens via the same deps
   * before resolving so the retry picks up the new values. The client
   * dedupes concurrent callers itself.
   */
  refresh: () => Promise<RefreshResult>
  /** Called when refresh fails (or is impossible) so the app can logout. */
  onAuthLost?: () => void
}

export interface ApiClientOptions {
  /** Origin like `https://dev.ez-qr.menu`. */
  baseURL: string
  deps: ApiClientDeps
  /** Optional override for tests. */
  refreshCoordinator?: RefreshCoordinator
  /** Pre-built axios instance, used by tests with mock adapters. */
  axiosInstance?: AxiosInstance
  /**
   * Per-request deadline in ms. A hung or very slow server otherwise
   * keeps a request open until the OS TCP timeout (minutes) — leaving
   * a Save button spinning forever. 20s comfortably covers a slow WP
   * write while still failing fast enough to show "try again".
   */
  timeoutMs?: number
  /**
   * Lifecycle hooks fired around every request (after interceptors,
   * once per network round-trip including refresh retries). Injected so
   * the app can drive a global "slow connection" indicator without the
   * client depending on any UI/store module. Omitted in tests.
   */
  onRequestStart?: () => void
  onRequestEnd?: () => void
}

const DEFAULT_TIMEOUT_MS = 20_000

export interface RequestOptions {
  /** Query string parameters. */
  query?: Record<string, string | number | boolean | undefined>
  /** JSON body. */
  body?: unknown
  /** Extra headers (overrides defaults if names collide). */
  headers?: Record<string, string>
  /**
   * Disable Bearer + refresh-on-401 for this call (used for `/auth/*` paths
   * by default — set explicitly only for testing or special cases).
   */
  skipAuth?: boolean
  /** AbortSignal for cancellation. */
  signal?: AbortSignal
}

/** Result of a WP REST list call — body plus pagination metadata. */
export interface PaginatedList<T> {
  items: T
  totalItems: number
  totalPages: number
}

/** Body + selected headers — for the few callers (auth) that need both. */
export interface RawResponse<T> {
  data: T
  /** Lowercased header names. */
  headers: Record<string, string>
}

export interface ApiClient {
  request<S extends ZodTypeAny>(
    method: HttpMethod,
    path: string,
    schema: S,
    options?: RequestOptions,
  ): Promise<z.infer<S>>
  get<S extends ZodTypeAny>(path: string, schema: S, options?: RequestOptions): Promise<z.infer<S>>
  post<S extends ZodTypeAny>(path: string, schema: S, options?: RequestOptions): Promise<z.infer<S>>
  patch<S extends ZodTypeAny>(
    path: string,
    schema: S,
    options?: RequestOptions,
  ): Promise<z.infer<S>>
  put<S extends ZodTypeAny>(path: string, schema: S, options?: RequestOptions): Promise<z.infer<S>>
  delete<S extends ZodTypeAny>(
    path: string,
    schema: S,
    options?: RequestOptions,
  ): Promise<z.infer<S>>
  /**
   * Like `post` but exposes response headers too — used by auth so we
   * can read the rotating CSRF token out of `X-EZJWT-CSRF`. Skips the
   * 401-refresh-retry path (login/refresh are themselves auth endpoints
   * and shouldn't recurse into refresh on a failure).
   */
  postRaw<S extends ZodTypeAny>(
    path: string,
    schema: S,
    options?: RequestOptions,
  ): Promise<RawResponse<z.infer<S>>>
  /**
   * GET a paginated WP REST list. Reads X-WP-Total / X-WP-TotalPages
   * (exposed via CORS by the server) so the UI can paginate without a
   * second round-trip. Falls back to inferred totals from the body when
   * the headers are absent.
   */
  list<S extends ZodTypeAny>(
    path: string,
    schema: S,
    options?: RequestOptions,
  ): Promise<PaginatedList<z.infer<S>>>
  /**
   * Trigger a session refresh through the shared single-flight
   * coordinator. Same `RefreshResult` the 401-retry path sees.
   *
   * Use this for boot-time silent refresh (`useSession.bootstrap`)
   * instead of calling `refreshRequest` directly — that bypass would
   * race against any concurrent 401-retry refresh, and the backend's
   * rotating-refresh-token reuse detection kills both sessions when
   * the same RT is presented twice (`ezjwt_refresh_reuse`).
   */
  refreshSession(): Promise<RefreshResult>
}

interface FlaggedConfig extends InternalAxiosRequestConfig {
  __ezqr_skipAuth?: boolean
}

export function createApiClient(opts: ApiClientOptions): ApiClient {
  const { baseURL, deps } = opts
  const coordinator = opts.refreshCoordinator ?? createRefreshCoordinator()

  /**
   * Single-fire latch for `onAuthLost`. The coordinator dedupes the
   * refresh round-trip itself, but every parallel 401-caller still
   * lands here when refresh resolves to `'failed'` — without this
   * latch we'd fire N logout callbacks for N concurrent 401s, leading
   * to N redirects / N toasts. Reset when a refresh later succeeds so
   * a future logout after re-auth still fires.
   */
  let authLostFired = false
  function notifyAuthLost() {
    if (authLostFired) return
    authLostFired = true
    deps.onAuthLost?.()
  }
  function noteRefreshSucceeded() {
    authLostFired = false
  }

  /**
   * Run the post-refresh retry of a request. The retry talks to the
   * server directly (no recursion back into rawRequest), so a SECOND
   * failure is terminal — we surface it. But a genuine 401 here means
   * even the freshly-refreshed session can't perform the call, so we
   * must fire `onAuthLost`. Crucially we gate that on `status === 401`:
   * a 400 that `errors.ts` reclassified to `kind: 'unauthorized'`
   * (rest_forbidden_status — a VALID session that simply lacks the
   * capability, e.g. reading drafts) must NOT log the user out.
   */
  async function retryAfterRefresh<T>(run: () => Promise<T>): Promise<T> {
    try {
      return await run()
    } catch (retryErr) {
      if (
        retryErr instanceof ApiError &&
        retryErr.kind === 'unauthorized' &&
        retryErr.status === 401
      ) {
        notifyAuthLost()
      }
      throw retryErr
    }
  }

  const instance: AxiosInstance =
    opts.axiosInstance ??
    axios.create({
      baseURL,
      // `text`, not `json`: we want the RAW body so `parseJsonResponse`
      // can decide. Left as `json`, axios runs its own lenient parse and
      // hands back a string on failure — exactly the case we need to
      // catch. The custom `transformResponse` replaces axios's default
      // JSON chain entirely.
      responseType: 'text',
      transformResponse: [parseJsonResponse],
      // Hung/slow server → fail fast with a timeout error instead of
      // an endlessly-spinning request. Tests inject their own
      // instance, so this only applies to the real app.
      timeout: opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
      // We set `withCredentials` per-request via the interceptor below.
    })

  // Request interceptor — Bearer, CSRF, cookies.
  instance.interceptors.request.use((config) => {
    const cfg = config as FlaggedConfig
    const url = cfg.url ?? ''
    const skipAuth = cfg.__ezqr_skipAuth === true || isAuthOpenPath(url)

    cfg.headers.set('Accept', 'application/json')
    if (!skipAuth) {
      const token = deps.getAccessToken()
      if (token) cfg.headers.set('Authorization', `Bearer ${token}`)
    }
    if (url === authPaths.refresh || url === authPaths.logout) {
      const csrf = deps.getCsrfToken()
      if (csrf) cfg.headers.set('X-CSRF-Token', csrf)
    }
    cfg.withCredentials = requiresCookies(url)
    return cfg
  })

  /**
   * Map an axios error to ApiError. Note: 401 still surfaces here as a
   * regular ApiError — refresh-retry happens at a layer above the axios
   * instance to keep the coordinator the single owner of dedupe logic.
   */
  function translateAxiosError(err: unknown): never {
    if (axios.isCancel(err) || (err instanceof Error && err.name === 'CanceledError')) {
      throw err
    }
    if (err instanceof AxiosError) {
      const method = err.config?.method?.toUpperCase()
      const url = err.config?.url
      const ctx = { ...(url && { url }), ...(method && { method }) }
      // axios reports its own timeout as ECONNABORTED (or a message
      // containing "timeout"). Surface it distinctly so the UI can say
      // "took too long" rather than "offline".
      if (!err.response && (err.code === 'ECONNABORTED' || /timeout/i.test(err.message))) {
        throw timeoutError(err, ctx)
      }
      if (!err.response) {
        throw networkError(err, ctx)
      }
      throw toApiError({
        status: err.response.status,
        body: err.response.data,
        headers: err.response.headers as Record<string, string>,
        ...ctx,
      })
    }
    throw networkError(err)
  }

  function buildConfig(
    method: HttpMethod,
    path: string,
    options: RequestOptions,
    skipAuth: boolean,
  ): AxiosRequestConfig & { __ezqr_skipAuth?: boolean } {
    const cfg: AxiosRequestConfig & { __ezqr_skipAuth?: boolean } = {
      method,
      url: path,
      __ezqr_skipAuth: skipAuth,
    }
    if (options.query) cfg.params = options.query
    if (options.body !== undefined) cfg.data = options.body
    if (options.headers) cfg.headers = options.headers
    if (options.signal) cfg.signal = options.signal
    return cfg
  }

  async function doFetchFull<T>(config: AxiosRequestConfig) {
    opts.onRequestStart?.()
    try {
      return await instance.request<T>(config)
    } catch (err) {
      translateAxiosError(err)
    } finally {
      opts.onRequestEnd?.()
    }
  }

  /**
   * Reject a successful (2xx) response whose body came back as the
   * non-JSON marker (a notice/HTML page that isn't our envelope). Done
   * once here so every fetch path — request, list, postRaw — gets the
   * guard without repeating it. The error carries the request url/method
   * and a capped raw snippet so the log can point at the offending file.
   */
  function assertJsonBody(res: { data: unknown; config: AxiosRequestConfig }): void {
    if (isNonJsonResponseMarker(res.data)) {
      const method = res.config?.method?.toUpperCase()
      const url = res.config?.url
      throw nonJsonResponseError(res.data.raw, {
        ...(url && { url }),
        ...(method && { method }),
      })
    }
  }

  async function doFetch<T>(config: AxiosRequestConfig): Promise<T> {
    const res = await doFetchFull<T>(config)
    assertJsonBody(res)
    return res.data
  }

  /**
   * Refresh proactively when we *know* the request needs auth but the
   * in-memory token is gone — e.g. the tab has been open longer than
   * the 15-minute access TTL, was backgrounded by the OS, or hot-
   * reloaded in dev. Without this the request lands at the server
   * anonymously and the server picks the failure mode: most of WP REST
   * returns 401 (which our 401-retry path below already handles), but
   * `wp/v2/<cpt>?status=draft` returns **400 `rest_forbidden_status`**
   * instead, because the param is rejected at validation time before
   * the auth check runs. That 400 escapes the 401-retry net and shows
   * up as a dead "Failed to load items" card.
   *
   * Returns `true` when we either had a token, or successfully got a
   * fresh one. `false` means no recovery is possible (refresh cookie
   * gone / expired) — caller should let the request go anyway so the
   * 401-retry path can fire `onAuthLost` consistently.
   */
  async function ensureAccessTokenIfNeeded(skipAuth: boolean): Promise<void> {
    if (skipAuth) return
    if (deps.getAccessToken() !== null) return
    const result = await coordinator.refresh(deps.refresh)
    if (result === 'ok') noteRefreshSucceeded()
    // No-op on 'failed'/'unreachable' — let the request through. It
    // either succeeds (token was fine) or comes back 401 and the retry
    // path below decides: server-rejected refresh → onAuthLost,
    // unreachable refresh → surface the error, keep the session.
  }

  async function rawRequest<S extends ZodTypeAny>(
    method: HttpMethod,
    path: string,
    schema: S,
    options: RequestOptions,
  ): Promise<z.infer<S>> {
    const skipAuth = options.skipAuth === true || isAuthOpenPath(path)
    await ensureAccessTokenIfNeeded(skipAuth)
    const config = buildConfig(method, path, options, skipAuth)

    let raw: unknown
    try {
      raw = await doFetch<unknown>(config)
    } catch (err) {
      if (err instanceof ApiError && err.kind === 'unauthorized' && !skipAuth) {
        const result = await coordinator.refresh(deps.refresh)
        if (result === 'ok') {
          noteRefreshSucceeded()
          raw = await retryAfterRefresh(() => doFetch<unknown>(config))
        } else {
          // Only a server-rejected refresh ('failed') is real auth loss.
          // 'unreachable' (offline / CORS-blocked refresh preflight) is
          // transient — surface the original 401 so the caller can show
          // an error and retry, but keep the session.
          if (result === 'failed') notifyAuthLost()
          throw err
        }
      } else {
        throw err
      }
    }
    const parsed = schema.safeParse(raw)
    if (!parsed.success) throw parseError(parsed.error)
    return parsed.data
  }

  const request: ApiClient['request'] = (method, path, schema, options = {}) =>
    rawRequest(method, path, schema, options)

  /** GET that also returns response headers (used by paginated lists). */
  async function rawListRequest<S extends ZodTypeAny>(
    path: string,
    schema: S,
    options: RequestOptions,
  ): Promise<PaginatedList<z.infer<S>>> {
    const skipAuth = options.skipAuth === true || isAuthOpenPath(path)
    await ensureAccessTokenIfNeeded(skipAuth)
    const config = buildConfig('GET', path, options, skipAuth)
    let res: Awaited<ReturnType<typeof doFetchFull<unknown>>>
    try {
      res = await doFetchFull<unknown>(config)
    } catch (err) {
      if (err instanceof ApiError && err.kind === 'unauthorized' && !skipAuth) {
        const result = await coordinator.refresh(deps.refresh)
        if (result === 'ok') {
          noteRefreshSucceeded()
          res = await retryAfterRefresh(() => doFetchFull<unknown>(config))
        } else {
          // See rawRequest: only 'failed' (server-rejected refresh) is
          // auth loss; 'unreachable' is transient and keeps the session.
          if (result === 'failed') notifyAuthLost()
          throw err
        }
      } else {
        throw err
      }
    }
    assertJsonBody(res)
    const parsed = schema.safeParse(res.data)
    if (!parsed.success) throw parseError(parsed.error)
    const totalItems = Number(res.headers['x-wp-total']) || 0
    const totalPages = Number(res.headers['x-wp-totalpages']) || 1
    return { items: parsed.data, totalItems, totalPages }
  }

  /**
   * Headers-aware POST. Skips the 401-refresh-retry path on purpose:
   * the only callers are login/refresh, which ARE the auth endpoints —
   * recursing into refresh on their failure would loop forever.
   */
  async function postRaw<S extends ZodTypeAny>(
    path: string,
    schema: S,
    options: RequestOptions = {},
  ): Promise<RawResponse<z.infer<S>>> {
    const skipAuth = options.skipAuth === true || isAuthOpenPath(path)
    await ensureAccessTokenIfNeeded(skipAuth)
    const config = buildConfig('POST', path, options, skipAuth)
    const res = await doFetchFull<unknown>(config)
    assertJsonBody(res)
    const parsed = schema.safeParse(res.data)
    if (!parsed.success) throw parseError(parsed.error)
    // axios lowercases response header names; copy into a plain object
    // so callers don't depend on AxiosHeaders' shape.
    const headers: Record<string, string> = {}
    for (const [k, v] of Object.entries(res.headers)) {
      if (typeof v === 'string') headers[k.toLowerCase()] = v
    }
    return { data: parsed.data, headers }
  }

  /**
   * Public single-flight entry to the same coordinator the 401-retry
   * path uses. Bootstrap, manual "refresh now" buttons, anything that
   * wants a session refresh should call this — direct `refreshRequest`
   * calls bypass the coordinator and risk firing the same RT twice,
   * which the backend's reuse detection turns into a revoked session.
   */
  async function refreshSession(): Promise<RefreshResult> {
    const result = await coordinator.refresh(deps.refresh)
    if (result === 'ok') noteRefreshSucceeded()
    return result
  }

  return {
    request,
    get: (path, schema, options) => request('GET', path, schema, options),
    post: (path, schema, options) => request('POST', path, schema, options),
    patch: (path, schema, options) => request('PATCH', path, schema, options),
    put: (path, schema, options) => request('PUT', path, schema, options),
    delete: (path, schema, options) => request('DELETE', path, schema, options),
    postRaw: (path, schema, options) => postRaw(path, schema, options),
    list: (path, schema, options = {}) => rawListRequest(path, schema, options),
    refreshSession,
  }
}
